/**
 * jsonLd.js — pure factory helpers that produce Schema.org JSON-LD payloads
 * for the local-SEO landing pages. No React, no DOM access — they are pure
 * functions consumed via `useJsonLd`.
 */

const ORIGIN = "https://triaxisxyz.com";

const ORG = {
  "@type": "LocalBusiness",
  "@id": `${ORIGIN}#org`,
  name: "Tri-Axis XYZ CAD Solutions, LLC",
  url: ORIGIN,
  email: "contact@triaxisxyz.com",
  telephone: "+91-80836-91588",
  image: `${ORIGIN}/favicon.svg`,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Orlando",
    addressRegion: "FL",
    addressCountry: "US",
  },
  areaServed: ["United States", "Canada", "Australia"],
  priceRange: "$$",
};

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${ORIGIN}${it.path}`,
    })),
  };
}

export function locationsIndexLd() {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "CAD Drafting & Land-Survey CAD Coverage — USA",
    description:
      "Nationwide CAD drafting and land-survey CAD services. Browse 50-state directory, ~280 counties and 25 metro hubs.",
    url: `${ORIGIN}/cad-services`,
    isPartOf: { "@id": `${ORIGIN}#org` },
    about: ORG,
  };
}

export function stateLd(state, services) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "CAD Drafting & Land-Survey CAD",
    provider: ORG,
    areaServed: {
      "@type": "State",
      name: state.state,
      containedInPlace: { "@type": "Country", name: "United States" },
    },
    name: `CAD Drafting Services in ${state.state}`,
    description: `Outsourced CAD drafting, Civil 3D, AutoCAD, MicroStation, Revit MEP and land-survey CAD production for firms across ${state.state}. ALTA, boundary, topo, plats, ROW, GIS, architectural, structural, MEP and civil drafting.`,
    url: `${ORIGIN}/cad-services/${state.slug}`,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `CAD services in ${state.state}`,
      itemListElement: services.slice(0, 12).map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.name },
        url: `${ORIGIN}/services/${s.slug}`,
      })),
    },
  };
}

export function countyLd(state, county) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "CAD Drafting & Land-Survey CAD",
    provider: ORG,
    areaServed: {
      "@type": "AdministrativeArea",
      name: county.name,
      containedInPlace: { "@type": "State", name: state.state },
    },
    name: `CAD Drafting Services — ${county.name}, ${state.abbr}`,
    description: `Land-survey CAD drafting, ALTA, boundary, topo, plats, MEP/BIM, structural and architectural drafting for firms in ${county.name}, ${state.state}. Serving ${county.seat} and ZIPs ${county.zips.slice(0, 4).join(", ")}.`,
    url: `${ORIGIN}/cad-services/${state.slug}/${county.slug}`,
  };
}

export function metroLd(metro) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "CAD Drafting & Land-Survey CAD",
    provider: ORG,
    areaServed: { "@type": "City", name: metro.name },
    name: `${metro.name} CAD Drafting & Survey CAD Services`,
    description: `CAD outsourcing partner for surveying, engineering and architectural firms in the ${metro.name}. Anchor counties: ${metro.anchor}.`,
    url: `https://triaxisxyz.com/metros/${metro.slug}`,
  };
}

export function articleLd(post) {
  const url = `${ORIGIN}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    keywords: [post.target_keyword, ...(post.tags || [])].filter(Boolean).join(", "),
    articleSection: post.category,
    author: { "@type": "Organization", name: post.author || "Tri-Axis XYZ Editorial" },
    publisher: ORG,
    mainEntityOfPage: url,
    url,
  };
}

export function disciplineLd(d) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: d.name,
    name: `${d.name} — Tri-Axis XYZ CAD Solutions`,
    description: d.intro,
    provider: ORG,
    areaServed: ["United States", "Canada", "Australia"],
    url: `${ORIGIN}/disciplines/${d.slug}`,
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
